import { useState, useEffect } from 'react'
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { IconSearch, IconBuilding, IconUser, IconLoader2 } from '@tabler/icons-react'
import { cn } from '@/lib/utils'
import { useClients } from '@/hooks/useClients'

interface ClientSearchProps {
  onSelect: (client: any) => void
  placeholder?: string
  className?: string
  value?: any
}

export function ClientSearch({
  onSelect,
  placeholder = "Buscar client o empresa...",
  className,
  value
}: ClientSearchProps) {
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const { data, isLoading } = useClients({
    page: 1,
    searchTerm: debouncedSearchTerm,
    actiuFilter: 'all',
    perPage: 10
  })

  const clients = data?.data || []

  const handleSelect = (client: any) => {
    onSelect(client)
    setOpen(false)
    setSearchTerm('')
  }

  const getDisplayName = (client: any) => {
    // El backend devuelve tipus_client = 'empresa' y empresa como nombre
    if (client.tipus_client === 'empresa' || client.empresa) {
      return client.empresa || client.nom_complet || 'Empresa sin nombre'
    }

    const name = client.nom_complet ||
      `${client.nom || ''} ${client.cognoms || ''}`.trim() ||
      client.email ||
      'Cliente sin nombre'
    return name
  }

  const getClientType = (client: any) => {
    // El backend devuelve tipus_client = 'empresa' o 'particular'
    if (client.tipus_client === 'empresa') {
      return {
        icon: IconBuilding,
        label: 'Empresa',
        color: 'bg-purple-100 text-purple-800'
      }
    }
    return {
      icon: IconUser,
      label: 'Particular',
      color: 'bg-blue-100 text-blue-800'
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
        >
          {value ? (
            <div className="flex items-center gap-2">
              {(() => {
                const type = getClientType(value)
                const Icon = type.icon
                return (
                  <>
                    <Icon className="w-4 h-4" />
                    <span>{getDisplayName(value)}</span>
                    {(value.num_document || value.nif_empresa) && (
                      <Badge variant="secondary" className="ml-2">
                        {value.num_document || value.nif_empresa}
                      </Badge>
                    )}
                  </>
                )
              })()}
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <IconSearch className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar per nom, empresa, NIF/CIF o email..."
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
          <CommandList>
            {isLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground flex items-center justify-center">
                <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                Buscant...
              </div>
            ) : searchTerm.length < 2 ? (
              <CommandEmpty>Escriu almenys 2 caràcters per buscar</CommandEmpty>
            ) : clients.length === 0 ? (
              <CommandEmpty>No s'han trobat clients</CommandEmpty>
            ) : (
              <CommandGroup>
                {clients.map((client) => {
                  const type = getClientType(client)
                  const Icon = type.icon

                  return (
                    <CommandItem
                      key={client.id}
                      onSelect={() => handleSelect(client)}
                      className="flex items-center justify-between py-3 cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <div className="font-medium">
                            {getDisplayName(client)}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {client.email || 'Sense email'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={cn("text-xs", type.color)}>
                          {type.label}
                        </Badge>
                        {(client.num_document || client.nif_empresa) && (
                          <Badge variant="outline" className="text-xs">
                            {client.tipus_document || 'ID'}: {client.num_document || client.nif_empresa}
                          </Badge>
                        )}
                      </div>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
